import { FC, useEffect, useState } from 'react';
import styles from './CookieBanner.module.scss';

const STORAGE_KEY = 'leopart.cookie.consent';
const CONSENT_VERSION = '1';

type ConsentValue = {
  version: string;
  acceptedAt: string;
  essential: true;
  analytics: boolean;
  marketing: boolean;
};

function readConsent(): ConsentValue | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentValue;
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeConsent(consent: ConsentValue) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  } catch {
    // localStorage non disponibile (browser strict): no-op.
  }
}

/**
 * Cookie consent banner — GDPR Step 4 conformità ePrivacy + art. 7
 * GDPR (consent informato).
 *
 * Mostra il banner finché l'utente non ha espresso scelta. Salva la
 * scelta in localStorage con versione (per gestire breaking changes
 * futuri della Privacy Policy: bumpare CONSENT_VERSION re-mostra
 * il banner a tutti).
 *
 * Categorie:
 * - `essential`: sempre on (auth, sessione, JWT). Required per
 *   funzionamento minimo. Non blocca su questi cookie/storage.
 * - `analytics`: futuro (Sentry/Plausible). Opt-in.
 * - `marketing`: futuro. Opt-in.
 *
 * "Accetta tutto" = essential+analytics+marketing.
 * "Solo essenziali" = solo essential.
 * "Personalizza" — placeholder, attualmente equivale a "Solo essenziali".
 */
export const CookieBanner: FC = () => {
  const [visible, setVisible] = useState(false);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [analyticsOn, setAnalyticsOn] = useState(false);
  const [marketingOn, setMarketingOn] = useState(false);

  useEffect(() => {
    const consent = readConsent();
    setVisible(!consent);
  }, []);

  const acceptAll = () => {
    writeConsent({
      version: CONSENT_VERSION,
      acceptedAt: new Date().toISOString(),
      essential: true,
      analytics: true,
      marketing: true,
    });
    setVisible(false);
  };

  const acceptEssentialOnly = () => {
    writeConsent({
      version: CONSENT_VERSION,
      acceptedAt: new Date().toISOString(),
      essential: true,
      analytics: false,
      marketing: false,
    });
    setVisible(false);
  };

  const acceptCustom = () => {
    writeConsent({
      version: CONSENT_VERSION,
      acceptedAt: new Date().toISOString(),
      essential: true,
      analytics: analyticsOn,
      marketing: marketingOn,
    });
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className={styles.banner} role="dialog" aria-label="Consenso cookie">
      <div className={styles.content}>
        <h3 className={styles.title}>Privacy &amp; Cookie</h3>
        <p className={styles.text}>
          LeoPart usa cookie e localStorage essenziali per autenticazione
          e funzionamento dell&apos;app. Per analytics e marketing
          chiediamo il tuo consenso esplicito. Puoi cambiare scelta in
          qualsiasi momento dalle impostazioni del tuo profilo.
          Maggiori dettagli nella{' '}
          <a href="/privacy" className={styles.link}>
            Privacy Policy
          </a>
          .
        </p>
        {customizeOpen && (
          <div className={styles.customize}>
            <label className={styles.toggleRow}>
              <input type="checkbox" checked disabled />
              <div>
                <strong>Essenziali</strong> (sempre attivi)
                <p>JWT, sessione, preferenze base. Necessari per il funzionamento.</p>
              </div>
            </label>
            <label className={styles.toggleRow}>
              <input
                type="checkbox"
                checked={analyticsOn}
                onChange={(e) => setAnalyticsOn(e.target.checked)}
              />
              <div>
                <strong>Analytics</strong>
                <p>Aiutaci a capire come usi l&apos;app per migliorarla (Sentry per error tracking — opzionale).</p>
              </div>
            </label>
            <label className={styles.toggleRow}>
              <input
                type="checkbox"
                checked={marketingOn}
                onChange={(e) => setMarketingOn(e.target.checked)}
              />
              <div>
                <strong>Marketing</strong>
                <p>Comunicazioni promozionali su nuove feature. Mai utilizzati per profilazione vendita a terzi.</p>
              </div>
            </label>
          </div>
        )}
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.btnSecondary}
            onClick={() => setCustomizeOpen((v) => !v)}
          >
            {customizeOpen ? 'Chiudi' : 'Personalizza'}
          </button>
          {customizeOpen ? (
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={acceptCustom}
            >
              Salva preferenze
            </button>
          ) : (
            <>
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={acceptEssentialOnly}
              >
                Solo essenziali
              </button>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={acceptAll}
              >
                Accetta tutto
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

/** Helper per altri componenti: leggi il consenso corrente. */
export function getCookieConsent(): ConsentValue | null {
  return readConsent();
}

/** Helper: ha consenso analytics? Usare prima di inizializzare Sentry/Plausible. */
export function hasAnalyticsConsent(): boolean {
  return readConsent()?.analytics ?? false;
}
